import { useContext } from 'react';

import { useSearchParams } from 'next/navigation';

import { isSold } from '../helpers/isSold';

import { RefererContext } from '@/modules/activityTracker/RefererProvider';
import {
  ActivityTrackerEventType,
  sessionLandingPageKey,
  UNKNOWN_LANDING_PAGE,
} from '@/modules/activityTracker/constants';
import { getReferer } from '@/modules/activityTracker/referers';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { segmentEventTypes } from '@/modules/externalTracking/types';
import { useExternalTracking } from '@/modules/externalTracking/useExternalTracking';
import {
  type ProductBySlugResponse,
  type ProductBySlugExtendedResponse,
} from '@/modules/product/aliases';
import { getProductViewTransitionFrom } from '@/modules/product/helpers/getProductViewTransitionFrom';
import { getSegmentProductData } from '@/modules/product/helpers/getSegmentProductData';
import {
  type BackendProductStatus,
  type ProductStatus,
} from '@/modules/product/types';
import { getLocalStorageItem } from '@/modules/storage';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

export function useProductTracking() {
  const searchParams = useSearchParams();
  const referer = useContext(RefererContext) || null;
  const [isAuthenticated, user] = useCurrentUser();
  const { sendActivityTrackerEvent } = useActivityTracker({ userId: user?.id });
  const {
    segment: { sendEvent: sendSegmentEvent },
    gtm: { sendEvent: sendGTMEvent },
  } = useExternalTracking();

  const isBoostedView = Boolean(
    isAuthenticated && searchParams.get('isBoostedView') === 'true'
  );

  const transitionFrom = getProductViewTransitionFrom(
    searchParams.get('from') || getReferer(referer),
    isBoostedView
  );

  function sendProductViewGTMEvent(
    productId: number,
    productPricing: ProductBySlugExtendedResponse['pricing'],
    eventId: string,
    productCategory?: string,
    variantId?: number
  ) {
    const item_id = variantId
      ? `${productId}-${variantId}`
      : productId.toString();
    const item_price = parseFloat(
      productPricing.original_price.price_breakdown.price.amount
    );
    sendGTMEvent({
      event: 'view_item',
      event_id: eventId,
      num_items: 1,
      content_ids: [item_id],
      ...(productCategory && { item_category: productCategory }),
      item_id,
      contents: [
        {
          id: item_id,
          item_price,
          quantity: 1,
        },
      ],
      ecommerce: {
        items: [
          {
            item_id,
            price: item_price,
          },
        ],
        currency: productPricing.currency,
        value: parseFloat(
          productPricing.original_price.price_breakdown.price.amount
        ),
      },
    });
  }

  function sendProductViewATEvent(
    productId: number,
    eventId: string,
    productPricing: ProductBySlugExtendedResponse['pricing'],
    productStatus?:
      ProductStatus | BackendProductStatus | ProductBySlugResponse['status']
  ) {
    sendActivityTrackerEvent(
      ActivityTrackerEventType.PRODUCT_VIEW,
      {
        schemaVersion: '6.0',
        productId,
        originalPrice: Number(
          productPricing.original_price.price_breakdown.price.amount
        ),
        discountedPrice:
          productPricing.discounted_price?.price_breakdown.price.amount &&
          Number(productPricing.discounted_price.price_breakdown.price.amount),
        taxInclusiveOriginalPrice:
          productPricing.original_price.total_price &&
          Number(productPricing.original_price.total_price),
        taxInclusiveDiscountedPrice:
          productPricing.discounted_price?.total_price &&
          Number(productPricing.discounted_price.total_price),
        currency: productPricing.currency,
        ...(productStatus && { isSold: isSold(productStatus) }),
      },
      {
        baseEventOverrides: {
          transitionFrom,
          id: eventId,
        },
      }
    );
  }

  function sendEngagedSessionActionATEvent() {
    const landingPage =
      getLocalStorageItem(sessionLandingPageKey) || UNKNOWN_LANDING_PAGE;

    sendActivityTrackerEvent(
      ActivityTrackerEventType.ENGAGED_SESSION_ACTION,
      {
        landingPage,
        schemaVersion: '1.0',
      },
      {
        baseEventOverrides: {
          transitionFrom,
        },
      }
    );
  }

  function sendWebModuleAttributionProductViewATEvent(
    productId: number,
    moduleOrigin: string,
    campaignTitle?: string | null
  ) {
    sendActivityTrackerEvent(
      ActivityTrackerEventType.WEB_MODULE_ATTRIBUTION_PRODUCT_VIEW,
      {
        productId,
        moduleName: moduleOrigin,
        campaignTitle,
        schemaVersion: '3.0',
      },
      {
        baseEventOverrides: {
          transitionFrom,
        },
      }
    );
  }

  function sendProductViewedSegmentedEvent(
    product: ProductBySlugResponse,
    productExtended: ProductBySlugExtendedResponse
  ) {
    const segmentProductData = getSegmentProductData(product, productExtended);

    if (productExtended.sizes) {
      productExtended.sizes.forEach((size) => {
        sendSegmentEvent(segmentEventTypes.PRODUCT_VIEWED, {
          ...segmentProductData,
          sku: size.id ? product.id + '-' + size.id : product.id.toString(),
        });
      });
    } else {
      sendSegmentEvent(segmentEventTypes.PRODUCT_VIEWED, segmentProductData);
    }
  }

  function sendProductInteractionActionATEvent(
    productId: number | undefined,
    interactionType: string,
    optionalParams?: {
      hashTag?: string;
      isBoosted?: boolean;
      brand?: string;
      imageCarouselIndex?: number;
    }
  ) {
    sendActivityTrackerEvent(
      ActivityTrackerEventType.PRODUCT_INTERACTION_ACTION,
      {
        schemaVersion: '4.0',
        productId,
        interactionType,
        ...optionalParams,
      }
    );
  }

  function sendBuyNowActionEvents(
    product: ProductBySlugResponse,
    productExtended: ProductBySlugExtendedResponse,
    callback?: () => void
  ) {
    const status = isAuthenticated ? 'Authenticated' : 'Guest';

    sendGTMEvent({
      category: 'Product',
      action: 'buy product',
      label: status,
    });

    const segmentProductData = getSegmentProductData(product, productExtended);

    sendSegmentEvent(segmentEventTypes.PRODUCT_ADDED, {
      ...segmentProductData,
      email: user?.email || '',
    });

    sendActivityTrackerEvent(ActivityTrackerEventType.BUY_NOW_ACTION, {
      productId: product.id,
      sellerId: product.seller.id,
    }).then(callback);
  }

  function sendBuyerProtectionLinkAction({
    productId,
    sellerId,
  }: {
    productId: number;
    sellerId: number;
  }) {
    sendActivityTrackerEvent(
      ActivityTrackerEventType.BUYER_PROTECTION_LINK_ACTION,
      {
        productId,
        sellerId,
        schemaVersion: '1.0',
      }
    );
  }

  function sendEditListingActionATEvent(productId: number) {
    sendActivityTrackerEvent(ActivityTrackerEventType.EDIT_LISTING_ACTION, {
      editProductId: productId,
    });
  }

  function sendDeleteProductActionATEvent(productId: number) {
    sendActivityTrackerEvent(ActivityTrackerEventType.DELETE_PRODUCT_ACTION, {
      productId,
    });
  }

  function sendEditProductGTMEvent() {
    sendGTMEvent({
      category: 'Listing',
      action: 'edit product',
    });
  }

  function sendConfirmDeleteProductGTMEvent() {
    sendGTMEvent({
      category: 'Listing',
      action: 'delete product',
    });
  }

  function sendCopyListingActionATEvent(productId: number) {
    sendActivityTrackerEvent(ActivityTrackerEventType.COPY_LISTING_ACTION, {
      copyProductId: productId,
    });
  }

  return {
    sendProductViewATEvent,
    sendProductViewGTMEvent,
    sendProductViewedSegmentedEvent,
    sendEngagedSessionActionATEvent,
    sendProductInteractionActionATEvent,
    sendWebModuleAttributionProductViewATEvent,
    sendBuyNowActionEvents,
    sendBuyerProtectionLinkAction,
    sendEditListingActionATEvent,
    sendDeleteProductActionATEvent,
    sendEditProductGTMEvent,
    sendConfirmDeleteProductGTMEvent,
    sendCopyListingActionATEvent,
  };
}
